import { FunctionComponent, useCallback, useMemo, useRef, useState } from "react";

import { Button, Card, Flex } from "@chakra-ui/react";
import { JSONSchemaType } from "ajv";
import { DynamicForm, ExpandedJSONSchema7, PageLoading } from "am-tax-fe-core";
import { throttle } from "lodash";

import { CreateDynamicFormArgs, DynamicFormResponse, UpdateDynamicFormArgs } from "../../api/questionnaires/api.ts";
import { AppPermissions, DynamicFormData } from "../../auth/Permissions.ts";
import { Mutation } from "../../utils/query-utils.ts";
import { Page } from "../Page.tsx";

import { removeEmptyProperties } from "./utils.ts";

export interface QuestionnaireProps {
    engagementId: string;
    projectId?: string | undefined;
    dynamicFormDefinitionId: string;
    goBack: () => void;
    isLoadingFormDefintions: boolean;
    schema?: string | null | undefined;
    isLoadingFormData: boolean;
    dynamicFormResponse?: DynamicFormResponse | undefined;
    createFormDataMutation: Mutation<CreateDynamicFormArgs, DynamicFormResponse>;
    updateFormDataMutation: Mutation<UpdateDynamicFormArgs, DynamicFormResponse>;
    appPermissions: AppPermissions;
}

type SchemaData = JSONSchemaType<ExpandedJSONSchema7>;

export const BaseQuestionnairePage: FunctionComponent<QuestionnaireProps> = ({
    engagementId,
    projectId,
    dynamicFormDefinitionId,
    isLoadingFormDefintions,
    schema,
    isLoadingFormData,
    dynamicFormResponse,
    createFormDataMutation,
    updateFormDataMutation,
    goBack,
    appPermissions,
}: QuestionnaireProps) => {
    const [isValid, setIsValid] = useState(false);

    const formSchema = schema ? (JSON.parse(schema) as JSONSchemaType<ExpandedJSONSchema7>) : undefined;
    const formData = dynamicFormResponse?.data;

    const defaultValues = useMemo(() => {
        return formData ? (JSON.parse(formData) as JSONSchemaType<SchemaData>) : undefined;
    }, [formData]);

    const [temporaryValues, setTemporaryValues] = useState<JSONSchemaType<JSONSchemaType<ExpandedJSONSchema7>> | undefined>(defaultValues);

    const save = async (fields: JSONSchemaType<SchemaData>, isDraft: boolean) => {
        if (dynamicFormResponse?.id) {
            const args = updateFormDataMutation.updateArgs({
                engagementId,
                projectId,
                dynamicFormId: dynamicFormResponse?.id,
                data: JSON.stringify(fields),
                isDraft,
            });
            await updateFormDataMutation.query.mutateAsync(args);
        } else {
            const args = createFormDataMutation.updateArgs({ engagementId, projectId, dynamicFormDefinitionId, data: JSON.stringify(fields), isDraft });
            await createFormDataMutation.query.mutateAsync(args);
        }
    };

    const handleSaveForLaterBtnClick = () => {
        if (temporaryValues) {
            const values = removeEmptyProperties(temporaryValues);
            save(values, true);
        }
    };

    // DEV-NOTE: We are using onChangeWithTrottle instead because the onChange from questionnaire for is firing continuously
    // TODO: Check why onChange in questionnaire is firing continuously
    // const onChange = async (fields: JSONSchemaType<SchemaData>) => {
    //     if (temporaryValues) {
    //         setTemporaryValues(fields);
    //     }
    // };

    const throttledChange = useRef(
        throttle((fields: JSONSchemaType<SchemaData>) => {
            setTemporaryValues(fields);
        }, 1000),
    ).current;

    const onChangeWithTrottle = useCallback(
        (fields: JSONSchemaType<SchemaData>) => {
            throttledChange(fields);
        },
        [throttledChange],
    );

    const onSubmit = async () => {
        if (temporaryValues) {
            await save(temporaryValues, false);
        }
    };

    const onIsValid = (valid: boolean) => {
        setIsValid(valid);
    };

    const isLoading = isLoadingFormDefintions || isLoadingFormData;
    const isUpdating = updateFormDataMutation.query.isPending || createFormDataMutation.query.isPending;

    return (
        <Page title="Questionnaire" style={{ viewTransitionName: "anything" }}>
            <Button mt="-1rem" mb="2rem" variant="outline" onClick={goBack}>
                Back to Questionnaires
            </Button>
            {isLoading && <PageLoading text={"Loading..."} />}

            {formSchema && !isLoading && (
                <Card p="1rem" maxWidth="1600px">
                    <DynamicForm
                        formSchema={formSchema}
                        onChange={onChangeWithTrottle}
                        defaultValues={defaultValues}
                        onSubmit={onSubmit}
                        showReset={false}
                        showSubmit={false}
                        isSubmitting={isUpdating}
                        onIsValid={onIsValid}
                    />
                    <Flex my="0.5rem" justifyContent="space-between">
                        {appPermissions.hasAny(DynamicFormData.create, DynamicFormData.update) && (
                            <>
                                <Button variant="ghost" onClick={handleSaveForLaterBtnClick}>
                                    Save For Later
                                </Button>
                                <Button variant="primary" onClick={onSubmit} isLoading={isUpdating} isDisabled={!isValid}>
                                    Submit
                                </Button>
                            </>
                        )}
                    </Flex>
                </Card>
            )}
        </Page>
    );
};
